import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Check } from 'lucide-react';
import { ResponsiveContainer, ComposedChart, Line, Area, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';

/* Ergebnis-Check-up, Schritt 3 „Dein Ergebnis nach 4 Wochen“ (Coach-Cockpit W1.6, backend/checkup_ergebnis.py).
   Block A Ergebnis (Start gegen heute, Kraft, Vorher/Nachher, Bilanz) · Block B Einschätzung des Coaches (Vorschlag vom
   Server, der Trainer bestätigt oder ändert — Pflicht, bevor der Kunde etwas sieht) · Block C Ergänzungsangebot (Preis,
   Vergleich und Ersparnis vom Server). Ruhig: keine Countdowns, keine Verknappung. Gerechnet wird hier nichts.
   Derselbe Baustein läuft im CRM, am Studio-Tablet und auf /coach — nur der Client ist ein anderer. Touch-Flächen ≥ 44 px. */

export const zahl = (v, st = 1) => (v == null ? '–' : Number(v).toLocaleString('de-DE', { minimumFractionDigits: st, maximumFractionDigits: st }));
export const euro = (v) => (v == null ? '–' : `${Number(v).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`);
export const tagText = (iso) => (iso ? `${String(iso).slice(8, 10)}.${String(iso).slice(5, 7)}.` : '');
export const fehlerText = (e) => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Das hat nicht geklappt. Bitte noch einmal versuchen.');
const vz = (v, st = 1) => (v == null ? '' : `${v > 0 ? '+' : v < 0 ? '−' : '±'}${zahl(Math.abs(v), st)}`);
const WERTUNG = { gut: 'text-emerald-600', schlecht: 'text-red-600' };

/* Markenfarbe: im CRM/auf /coach `--marke`, am Tablet (ohne Variable) das P1-Rot */
export function markeFarbe() {
  try {
    const v = getComputedStyle(document.documentElement).getPropertyValue('--marke').trim();
    return v ? `hsl(${v})` : '#E60000';
  } catch { return '#E60000'; }
}
function leise() {
  try { const v = getComputedStyle(document.documentElement).getPropertyValue('--muted-foreground').trim(); return v ? `hsl(${v})` : '#6B7280'; } catch { return '#6B7280'; }
}

export function Taste({ ak, rot, children, className = '', ...p }) {
  return (
    <button type="button" {...p}
      className={`min-h-[48px] px-5 rounded-xl text-base font-semibold border border-border active:opacity-80 disabled:opacity-50 ${rot ? ak?.className || '' : ''} ${className}`}
      style={rot ? ak?.style : undefined}>{children}</button>
  );
}

/* Ein Foto über den Client laden (Sitzung im Kopf, kein offener Link) */
function Foto({ client, id, alt }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let weg = false; let u = null;
    if (!client?.foto) return undefined;
    client.foto(id).then((r) => { if (weg) return; u = URL.createObjectURL(r.data); setUrl(u); }).catch(() => {});
    return () => { weg = true; if (u) URL.revokeObjectURL(u); };
  }, [client, id]);
  return url ? <img src={url} alt={alt} className="w-full aspect-[3/4] object-cover rounded-xl bg-muted" />
    : <div className="w-full aspect-[3/4] rounded-xl bg-muted" />;
}

/* Block A — auch im Abschluss-Check-up (3 Monate) */
export function ErgebnisBlockA({ e, client }) {
  const haupt = (e.messwerte || []).filter((m) => m.haupt);
  const weitere = (e.messwerte || []).filter((m) => !m.haupt);
  const b = e.bilanz || {};
  return (
    <section className="space-y-4" data-testid="ergebnis-block-a">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-xl font-bold">Start gegen heute</h3>
        <span className="text-sm text-muted-foreground">{tagText(e.zeitraum?.von)} – {tagText(e.zeitraum?.bis)} · {e.zeitraum?.tage} Tage</span>
      </div>
      {haupt.length === 0 && <p className="text-base text-muted-foreground">Keine Messwerte zu Start und heute.</p>}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {haupt.map((m) => (
          <div key={m.key} className="rounded-2xl border border-border p-4" data-testid={`ergebnis-${m.key}`}>
            <p className="text-sm text-muted-foreground">{m.label}</p>
            <p className="text-2xl font-bold tabular-nums">{zahl(m.jetzt)} <span className="text-base font-normal">{m.einheit}</span></p>
            <p className="text-sm text-muted-foreground tabular-nums">Start {zahl(m.start)} {m.einheit}</p>
            {m.delta != null && (
              <p className={`text-sm font-semibold tabular-nums ${WERTUNG[m.wertung] || ''}`}>
                {vz(m.delta)} {m.einheit}{m.prozent != null ? ` (${vz(m.prozent)} %)` : ''}
              </p>
            )}
          </div>
        ))}
      </div>
      {weitere.length > 0 && (
        <p className="text-sm text-muted-foreground">
          {weitere.map((m) => `${m.label} ${zahl(m.start)} → ${zahl(m.jetzt)} ${m.einheit}`).join(' · ')}
        </p>
      )}
      {(e.kraft || []).length > 0 && (
        <div className="space-y-2">
          <p className="text-lg font-semibold">Kraft</p>
          <ul className="rounded-2xl border border-border divide-y divide-border">
            {e.kraft.map((k) => (
              <li key={k.test} className="p-3 flex flex-wrap justify-between gap-2 text-base">
                <span>{k.titel}</span>
                <span className="tabular-nums">{zahl(k.start)} → <b>{zahl(k.jetzt)}</b> {k.einheit}
                  {k.prozent != null && <span className={k.urteil === 'besser' ? 'text-emerald-600' : k.urteil === 'schlechter' ? 'text-red-600' : ''}> ({vz(k.prozent)} %)</span>}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {(e.fotos || []).length > 0 && (
        <div className="space-y-2" data-testid="ergebnis-fotos">
          <p className="text-lg font-semibold">Vorher / Nachher</p>
          <div className="grid gap-3 sm:grid-cols-2">
            {e.fotos.map((f) => (
              <div key={f.ansicht} className="grid grid-cols-2 gap-2">
                <figure><Foto client={client} id={f.start.id} alt="Vorher" /><figcaption className="text-sm text-muted-foreground mt-1">Vorher · {tagText(f.start.datum)}</figcaption></figure>
                <figure><Foto client={client} id={f.jetzt.id} alt="Nachher" /><figcaption className="text-sm text-muted-foreground mt-1">Heute · {tagText(f.jetzt.datum)}</figcaption></figure>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">Nur mit Einwilligung des Kunden – nur für das Gespräch und den Kunden selbst.</p>
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="ergebnis-bilanz">
        <div className="rounded-2xl bg-muted/50 p-4"><p className="text-sm text-muted-foreground">Trainingstage</p><p className="text-2xl font-bold tabular-nums">{b.training?.gesamt ?? 0}</p></div>
        <div className="rounded-2xl bg-muted/50 p-4"><p className="text-sm text-muted-foreground">Kraftlog / Studio</p><p className="text-2xl font-bold tabular-nums">{b.training?.kraftlog ?? 0} / {b.training?.studio ?? 0}</p></div>
        <div className="rounded-2xl bg-muted/50 p-4"><p className="text-sm text-muted-foreground">Check-ins</p><p className="text-2xl font-bold tabular-nums">{b.checkins?.beantwortet ?? 0}<span className="text-base font-normal"> von {b.checkins?.faellig ?? 0}</span></p></div>
        <div className="rounded-2xl bg-muted/50 p-4"><p className="text-sm text-muted-foreground">Ernährungstage im Ziel</p><p className="text-2xl font-bold tabular-nums">{b.ernaehrung ? <>{b.ernaehrung.im_ziel}<span className="text-base font-normal"> von {b.ernaehrung.mit_ziel}</span></> : '–'}</p></div>
      </div>
    </section>
  );
}

/* Eine Linie nach vorn: echter Verlauf durchgezogen, danach die Spanne gestrichelt — keine Vergleichslinie. */
export function EinschaetzungChart({ feld }) {
  const daten = useMemo(() => {
    const zeit = (iso) => new Date(`${String(iso).slice(0, 10)}T12:00:00`).getTime();
    const echt = (feld.echt || []).map((p) => ({ x: zeit(p.datum), echt: p.wert }));
    const spanne = (feld.spanne || []).map((p) => ({ x: zeit(p.datum), band: [p.von, p.bis] }));
    const je = new Map();
    [...echt, ...spanne].forEach((p) => je.set(p.x, { ...(je.get(p.x) || { x: p.x }), ...p }));
    return [...je.values()].sort((a, b) => a.x - b.x);
  }, [feld]);
  if (daten.length < 2) return null;
  const marke = markeFarbe();
  const grau = leise();
  return (
    <div className="h-48 w-full" data-testid="einschaetzung-chart">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={daten} margin={{ top: 8, right: 12, bottom: 0, left: -12 }}>
          <CartesianGrid stroke={grau} strokeOpacity={0.15} vertical={false} />
          <XAxis dataKey="x" type="number" scale="time" domain={['dataMin', 'dataMax']} tick={{ fontSize: 12, fill: grau }}
            tickFormatter={(v) => new Date(v).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })} />
          <YAxis domain={['auto', 'auto']} tick={{ fontSize: 12, fill: grau }} width={48} />
          <Tooltip labelFormatter={(v) => new Date(v).toLocaleDateString('de-DE')}
            formatter={(v, n) => (Array.isArray(v) ? [`${zahl(v[0])} bis ${zahl(v[1])} ${feld.einheit}`, 'Einschätzung'] : [`${zahl(v)} ${feld.einheit}`, n === 'echt' ? 'Gemessen' : n])} />
          <Area dataKey="band" stroke={marke} strokeDasharray="5 4" fill={marke} fillOpacity={0.12} connectNulls isAnimationActive={false} />
          <Line dataKey="echt" stroke={marke} strokeWidth={2.5} dot={{ r: 3 }} connectNulls isAnimationActive={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

const FELDER = [['gewicht', 'gewicht_kg', 'Gewicht', 'kg'], ['taille', 'taille', 'Taille', 'cm'], ['fett', 'fett_prozent', 'Körperfett', '%'], ['muskel', 'muskelmasse_kg', 'Muskelmasse', 'kg']];

function Einschaetzung({ r, client, ak, onNeu }) {
  const e = r.einschaetzung || {};
  const basis = e.final || e.vorschlag || {};
  const felder = FELDER.filter(([k]) => (e.vorschlag || {})[`${k}_von`] != null || (e.final || {})[`${k}_von`] != null);
  const [werte, setWerte] = useState(() => Object.fromEntries(felder.flatMap(([k]) => [[`${k}_von`, basis[`${k}_von`] ?? ''], [`${k}_bis`, basis[`${k}_bis`] ?? '']])));
  const [meilen, setMeilen] = useState(() => (e.meilensteine?.length ? [...e.meilensteine] : ['']));
  const [satz, setSatz] = useState(e.coach_satz || '');
  const [arbeitet, setArbeitet] = useState(false);
  const [fehler, setFehler] = useState('');
  const vorschlaege = (r.ergebnis?.kraft || []).filter((k) => k.urteil === 'besser').map((k) => `${k.titel}: über ${zahl(k.jetzt)} ${k.einheit}`);
  const tu = async (fn) => {
    setArbeitet(true); setFehler('');
    try { const neu = (await fn()).data; onNeu(neu); return neu; } catch (x) { setFehler(fehlerText(x)); return null; } finally { setArbeitet(false); }
  };
  const bestaetigen = () => tu(() => client.einschaetzung({
    final: Object.fromEntries(Object.entries(werte).filter(([, v]) => String(v).trim() !== '')),
    meilensteine: meilen.map((m) => m.trim()).filter(Boolean), coach_satz: satz,
  }));
  const bereit = satz.trim() && meilen.some((m) => m.trim());
  return (
    <section className="space-y-4 rounded-2xl border border-border p-4 sm:p-5" data-testid="ergebnis-block-b">
      <div>
        <h3 className="text-xl font-bold">{r.texte?.einschaetzung_titel}</h3>
        <p className="text-sm text-muted-foreground">{r.texte?.einschaetzung_hinweis}</p>
      </div>
      {Object.entries(e.chart || {}).map(([k, f]) => (
        <div key={k}><p className="text-sm font-semibold">{f.label}</p><EinschaetzungChart feld={f} /></div>
      ))}
      {felder.length > 0 ? (
        <div className="space-y-3">
          <p className="text-base font-semibold">In 3 Monaten etwa</p>
          {felder.map(([k, , label, einheit]) => (
            <div key={k} className="flex flex-wrap items-center gap-2 text-base" data-testid={`spanne-${k}`}>
              <span className="w-32">{label}</span>
              <input inputMode="decimal" value={werte[`${k}_von`]} onChange={(x) => setWerte({ ...werte, [`${k}_von`]: x.target.value })}
                className="min-h-[48px] w-24 rounded-xl border border-border bg-background px-3 tabular-nums" aria-label={`${label} von`} />
              <span>bis</span>
              <input inputMode="decimal" value={werte[`${k}_bis`]} onChange={(x) => setWerte({ ...werte, [`${k}_bis`]: x.target.value })}
                className="min-h-[48px] w-24 rounded-xl border border-border bg-background px-3 tabular-nums" aria-label={`${label} bis`} />
              <span className="text-sm text-muted-foreground">{einheit}</span>
              {e.vorschlag?.[`${k}_von`] != null && <span className="text-sm text-muted-foreground">Vorschlag {zahl(e.vorschlag[`${k}_von`])}–{zahl(e.vorschlag[`${k}_bis`])}</span>}
            </div>
          ))}
          <p className="text-xs text-muted-foreground">Vorschlag aus dem Verlauf der ersten Wochen, je Folgemonat um {zahl((e.abschwaechung ?? 0.8) * 100, 0)} % abgeschwächt, als Spanne ± {zahl(e.spanne_pct ?? 25, 0)} %. Bitte prüfen und anpassen.</p>
        </div>
      ) : <p className="text-base text-muted-foreground">Für eine Spanne fehlen Start- oder Heute-Werte. Meilensteine und Satz reichen.</p>}
      <div className="space-y-2">
        <p className="text-base font-semibold">Meilensteine für Monat 2 und 3 *</p>
        {meilen.map((m, i) => (
          <input key={i} value={m} maxLength={300} onChange={(x) => setMeilen(meilen.map((y, j) => (j === i ? x.target.value : y)))}
            className="w-full min-h-[48px] rounded-xl border border-border bg-background px-3 text-base" data-testid={`meilenstein-${i}`} />
        ))}
        <div className="flex flex-wrap gap-2">
          {meilen.length < 3 && <button type="button" className="min-h-[44px] px-3 rounded-xl border border-dashed border-border text-sm" onClick={() => setMeilen([...meilen, ''])}>+ Meilenstein</button>}
          {vorschlaege.map((v) => (
            <button key={v} type="button" className="min-h-[44px] px-3 rounded-xl border border-dashed border-border text-sm"
              onClick={() => setMeilen((x) => { const leer = x.findIndex((y) => !y.trim()); if (leer >= 0) return x.map((y, j) => (j === leer ? v : y)); return x.length < 3 ? [...x, v] : x; })}>+ {v}</button>
          ))}
        </div>
      </div>
      <div className="space-y-2">
        <p className="text-base font-semibold">Ein Satz von dir *</p>
        {(e.bausteine || []).length > 0 && (
          <div className="flex flex-wrap gap-2">{e.bausteine.map((t) => (
            <button key={t} type="button" className="min-h-[44px] px-3 rounded-xl border border-dashed border-border text-sm text-left" onClick={() => setSatz(t)}>{t}</button>
          ))}</div>
        )}
        <textarea value={satz} onChange={(x) => setSatz(x.target.value)} rows={2} maxLength={300} data-testid="coach-satz"
          className="w-full rounded-xl border border-border bg-background p-3 text-base" />
      </div>
      {fehler && <p className="rounded-xl bg-red-50 text-red-700 p-3 text-base" role="alert">{fehler}</p>}
      <div className="flex flex-wrap items-center gap-3">
        <Taste ak={ak} rot disabled={arbeitet || !bereit} onClick={bestaetigen} data-testid="einschaetzung-bestaetigen">
          {e.bestaetigt ? 'Änderung bestätigen' : 'Einschätzung bestätigen'}</Taste>
        <Taste ak={ak} disabled={arbeitet} onClick={() => tu(() => client.einschaetzungVorschlag())}>Vorschlag neu</Taste>
        {e.bestaetigt && <span className="flex items-center gap-1 text-sm font-semibold" data-testid="einschaetzung-bestaetigt"><Check className="w-4 h-4" /> bestätigt{e.bestaetigt_von?.name ? ` von ${e.bestaetigt_von.name}` : ''}</span>}
      </div>
      {!e.bestaetigt && <p className="text-sm text-muted-foreground">Der Kunde sieht die Einschätzung erst, wenn du sie bestätigt hast.</p>}
    </section>
  );
}

/* Block C — die Ergänzung (Vorlage `ergaenzung_2m`) */
function Ergaenzung({ k, titel, gewaehlt, onWahl, ak }) {
  if (!k) return null;
  return (
    <section className={`rounded-2xl border-2 p-4 sm:p-5 space-y-2 ${gewaehlt ? ak?.rahmen || '' : 'border-border'}`} style={gewaehlt ? ak?.rahmenStyle : undefined}
      data-testid="ergebnis-block-c">
      <h3 className="text-xl font-bold">{titel}</h3>
      {k.kurz && <p className="text-base text-muted-foreground">{k.kurz}</p>}
      <p className="text-base tabular-nums">
        {k.vergleichspreis != null && <s className="text-muted-foreground mr-2">{euro(k.vergleichspreis)}</s>}
        <b className="text-2xl">{euro(k.preis)}</b> pro Monat
      </p>
      {k.ersparnis ? <p className="text-base font-semibold" data-testid="ergaenzung-ersparnis">Du sparst {euro(k.ersparnis)} gegenüber Einzelbuchung</p> : null}
      <p className="text-sm text-muted-foreground">{k.monate || 2} Monatsrechnungen{k.termine ? ` · je Monat ${k.termine} PT-Termine` : ''} · Ernährungscoaching inklusive</p>
      <div className="flex flex-wrap gap-2 pt-1">
        <button type="button" aria-pressed={gewaehlt} onClick={() => onWahl(!gewaehlt)} data-testid="ergaenzung-waehlen"
          className={`min-h-[48px] px-5 rounded-xl border text-base ${gewaehlt ? `${ak?.className || ''} font-semibold` : 'border-border'}`} style={gewaehlt ? ak?.style : undefined}>
          {gewaehlt ? '✓ Ergänzung gewählt' : 'Ergänzung wählen'}</button>
      </div>
    </section>
  );
}

/* Schritt 3 des Ergebnis-Check-ups. `onWeiter(auswahl)` → Schritt 4 (Abschluss) mit der gewählten Karte (oder ohne). */
export default function ErgebnisSeite({ client, ak, onWeiter }) {
  const [r, setR] = useState(null);
  const [fehler, setFehler] = useState('');
  const [gewaehlt, setGewaehlt] = useState(true);
  const laden = useCallback(async () => {
    try { setR((await client.ergebnis()).data); setFehler(''); } catch (e) { setFehler(fehlerText(e)); }
  }, [client]);
  useEffect(() => { laden(); }, [laden]);
  if (!r) return <div className="py-10 flex justify-center">{fehler ? <p className="text-red-600">{fehler}</p> : <Loader2 className="w-8 h-8 animate-spin" />}</div>;
  const bestaetigt = !!r.einschaetzung?.bestaetigt;
  return (
    <section className="space-y-6" data-testid="schritt-ergebnis">
      <h2 className="text-2xl font-bold">{r.titel}</h2>
      <ErgebnisBlockA e={r.ergebnis} client={client} />
      <Einschaetzung key={r.einschaetzung?.bestaetigt_am || 'neu'} r={r} client={client} ak={ak} onNeu={setR} />
      <Ergaenzung k={r.ergaenzung} titel={r.texte?.ergaenzung_titel} gewaehlt={gewaehlt && !!r.ergaenzung} onWahl={setGewaehlt} ak={ak} />
      <div className="flex flex-wrap gap-3">
        <Taste ak={ak} rot disabled={!bestaetigt} onClick={() => onWeiter(gewaehlt && r.ergaenzung ? [r.ergaenzung.schluessel] : [])} data-testid="weiter-abschluss">
          {gewaehlt && r.ergaenzung ? 'Weiter zum Abschluss' : 'Ohne Ergänzung weiter'}</Taste>
      </div>
      {!bestaetigt && <p className="text-sm text-muted-foreground">Erst die Einschätzung bestätigen – dann geht es zum Abschluss.</p>}
    </section>
  );
}
